import React, { useCallback, useState, forwardRef } from 'react';
import { cloneDeep, isEqual } from 'lodash';

import { useDispatch, useSelector } from 'react-redux';
import { setSearchAreaCriteria, setSearchAreaCriteriaAdvancedMode } from '../../../../../redux/search/Actions';

import { Box, makeStyles, Switch, FormControlLabel, Typography } from '@material-ui/core';

import SimpleHeading from '../../common/SimpleHeading';

import ConfirmationDialog from '../../../../Common/UI/ConfirmationDialog';
import { SEARCH_REDUCER } from '../../../../../redux/ReducerNames';
import QuickCriteria from '../../common/searcharea/Criteria';
import AdvancedCriteria from './query/Group';
import CleanCriteria from '../../../../../redux/search_area/CleanCriteria';


const useStyles = makeStyles((theme) => style(theme));

function SearchCriteria ({  }, ref) {
    const classes = useStyles();
    const search = useSelector(state => state.search);
    
  
    const isSearchDirty = useSelector(state => state.search.isSearchDirty);

    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
    const toggleConfirmationDialog = () => setIsConfirmationDialogOpen(p=>!p);
    const handleOpenConfirmationDialog = () => setIsConfirmationDialogOpen(true);
    const handleCloseConfirmationDialog = () => setIsConfirmationDialogOpen(false);

    const dispatch = useDispatch();
    
    const layerId = useSelector(state => state.search.searchArea.layerId);
    const layer = useSelector(state => state.layers.layers.find(el => el.id === layerId));
    const fields = useSelector(state => ({
        ...state.layers.searchLayers,
        ...(layer.filterConfig ? layer.filterConfig.fields : {})
    }));

    const isAdvancedSearch = useSelector(state => state.search.searchArea.isCriteriaAdvancedMode);
    const originalCriteria = useSelector(state => state.search.searchArea.criteria);
    const dispatchSetSearchCriteriaAdvancedMode = (isAdvanced) => dispatch(setSearchAreaCriteriaAdvancedMode(isAdvanced));
    const dispatchSetSearchCriteria = (criteria) => dispatch(setSearchAreaCriteria(criteria));

    const searchByLayerId = useSelector(state => state.search.searchArea.layerId);
    const searchFeatures = useSelector(state => state.user.features['siteFinder']);
    const searchLayerFeature = searchFeatures.layers.find(l => l.layerId === searchByLayerId);
    const advancedCriteriaEnabled = searchLayerFeature.advancedCriteriaEnabled;

    const criteria = cloneDeep(originalCriteria)
    
    function confirmToggleAdvancedSearch(){
        dispatchSetSearchCriteriaAdvancedMode(!isAdvancedSearch); 
        if(isConfirmationDialogOpen){ 
            toggleConfirmationDialog();
        };
    }

    function toggleAdvancedSearch() {
        if (isAdvancedSearch && !isEqual(criteria, CleanCriteria(criteria, fields, 'simple_search'))) {
            toggleConfirmationDialog();
            return;
        } else {           
            const currentChildren = criteria.children      
            const newChildren = cleanChildren(currentChildren)      
            criteria.children = newChildren       

            dispatchSetSearchCriteria(criteria);                              
            confirmToggleAdvancedSearch();
        }         
    }

    function cleanChildren(currentChildren){
        let requiredChildren = []
        const defaultTree = layer.filterConfig ? layer.filterConfig.defaultTree : {}            
        const defaultTreeRequiredFields = defaultTree.children.filter(child => fields[child.field]?.isRequired)            
          
        defaultTreeRequiredFields.forEach(rule => {
            const foundChild = currentChildren.find(child => child.field === rule.field)                
            if(!foundChild) requiredChildren.push(rule)
           
            if(foundChild) {    
                if(!foundChild.id.includes('required')) foundChild.id = `required_${foundChild.field}`

                const idx = currentChildren.findIndex(item => item.field === foundChild.field)
                const child = currentChildren.splice(idx, 1)
                requiredChildren.unshift(child[0])
                }
            })

        return [...requiredChildren, ...currentChildren]
    }

    const handleChange = useCallback((tree)=>{
        dispatchSetSearchCriteria(tree);
    },[]);

    const handleDelete = useCallback(()=>{ 

        const newTree = {
            id: 'root',
            type: 'group',
            conjunction: 'and',
            not: false,
            children: []
        }
        
        const requiredChildren = criteria.children.filter(child => 
            fields[child.field]?.isRequired && child.id.includes('required')
        );
        
        newTree.children = requiredChildren
       
        dispatchSetSearchCriteria(newTree);
    },[criteria.children])

    return (
        <div ref={ref} className={classes.root}>
            <Box
                display="flex"
                justifyContent="space-between"
            >
                <SimpleHeading
                    label="3. Choose YOUR SEARCH CRITERIA"
                />
                {
                    advancedCriteriaEnabled && 
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems='center'
                        className={classes.switchContainer}
                    >
                        <Typography>
                            Advanced Search
                        </Typography>
                        <Switch
                            checked={isAdvancedSearch}
                            onChange={toggleAdvancedSearch}
                            className={classes.muiSwitch}
                        />
                    </Box>
                }
                
            </Box>
            
            {
                (isAdvancedSearch && advancedCriteriaEnabled) ?  
                    <AdvancedCriteria
                        tree={criteria}
                        fields={fields}
                        onChange={handleChange}
                        onDelete={handleDelete}
                    /> :
                    <QuickCriteria
                        reducerName={SEARCH_REDUCER}
                        reducerAreaKey="searchArea"
                        tag="simple_search"
                        layerId={layerId}
                    />
            }

            <ConfirmationDialog 
                open={isConfirmationDialogOpen}
                onClose={handleCloseConfirmationDialog}
                titleString='Switch to quick search'
                contentString='Clicking this toggle will lose some of your current advanced search rules. Are you sure you want to continue?'
                confirmationLabel='Continue'
                onConfirm={confirmToggleAdvancedSearch}
            />
        </div>
    );
}

const style = theme =>({
    root: {
        '&> *:not(:last-child)':{
            marginBottom: theme.constants.verticalMargin
        }
    },
    switchContainer: {
        color: theme?.palette?.text?.secondary
    },
    muiSwitch: {
        marginLeft: 4
    }
});

export default forwardRef(SearchCriteria);